/**
 * @fileoverview AR-924 — Body of the teacher deliverable drawer once loaded: sections for the three
 * submission modes (see submissionMode.ts); all writes come from the drawer's hook state.
 */

import { Divider, Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { ClassroomDeliverableSyncSection } from 'components/ClassroomDeliverable'
import type { TaskTrackDeliverable } from '../../types'
import { DeliverableInfoCard } from './DeliverableInfoCard'
import { DeliverableTaskHeader } from './DeliverableTaskHeader'
import { FilesAndLinksSection } from './FilesAndLinksSection'
import { SubmissionHeader, UploadedByBanner } from './SubmissionHeader'
import { TeacherUploadArea } from './TeacherUploadArea'
import { WorkCompletedOnField } from './WorkCompletedOnField'
import { deliveryStatusOf, dueDayOf, type SubmissionMode } from './submissionMode'
import type { TeacherDeliverableState } from './useTeacherDeliverable'

interface DeliverableContentProps {
  readonly data: TaskTrackDeliverable
  readonly mode: SubmissionMode
  readonly state: TeacherDeliverableState
  readonly studentId: string
  readonly studentFirstName: string
  readonly projectFinished: boolean
  /** Date picked before the first upload (`YYYY-MM-DD`). */
  readonly pickedDate: string | null
  readonly onPickDate: (isoDay: string) => void
  readonly onDateEditingChange: (dirty: boolean) => void
  readonly onFiles: (files: File[]) => void
  readonly onAddLink: () => void
}

export function DeliverableContent(props: DeliverableContentProps) {
  const { data, mode, state, studentId, studentFirstName, projectFinished } = props
  const { pickedDate, onPickDate, onDateEditingChange, onFiles, onAddLink } = props
  const { t } = useTranslation()
  const tr = (key: string, options?: object) => t(`task_tracker.teacher_upload.${key}`, options)

  const delivery = data.delivery
  const canWrite = mode !== 'student' && !projectFinished
  const notSubmitted = mode === 'not_submitted'
  const uploadedByTeacher = mode === 'teacher'

  return (
    <Stack spacing="md">
      <DeliverableInfoCard
        projectName={data.project.name}
        lessonTitle={data.sessionMeeting.title}
        activityTitle={data.activity.title}
      />
      {data.task.classroomAssignment && (
        <ClassroomDeliverableSyncSection
          projectId={data.project.id}
          studentId={studentId}
          state={data.task.classroomAssignment}
          onSynced={state.reload}
        />
      )}
      <DeliverableTaskHeader task={data.task} />
      <Divider my="xs" />

      <Stack spacing="sm">
        <SubmissionHeader
          studentFirstName={studentFirstName}
          status={deliveryStatusOf(data)}
          explainStatus={uploadedByTeacher}
        />
        {projectFinished && <Text size="sm" color="dimmed">{tr('project_finished')}</Text>}
        {notSubmitted && !projectFinished && (
          <Text size="sm" color="dimmed">{tr('class_hint', { name: studentFirstName })}</Text>
        )}
        {mode === 'student' && <Text size="sm" color="dimmed">{tr('student_submitted_readonly')}</Text>}
        {uploadedByTeacher && delivery?.uploadedBy && (
          <UploadedByBanner
            uploadedBy={delivery.uploadedBy}
            studentFirstName={studentFirstName}
            submittedAt={delivery.submittedAt}
          />
        )}
        {uploadedByTeacher && canWrite && (
          <WorkCompletedOnField
            mode="summary"
            value={delivery?.workCompletedAt ?? null}
            saving={state.savingDate}
            onSave={state.saveWorkCompletedAt}
            onEditingChange={onDateEditingChange}
          />
        )}
      </Stack>

      {canWrite && (
        <TeacherUploadArea
          showDropzone={notSubmitted}
          uploadPercent={state.uploadPercent}
          disabled={state.busy}
          onFiles={onFiles}
          onAddLink={onAddLink}
        />
      )}
      {notSubmitted && canWrite && (
        <WorkCompletedOnField mode="picker" value={pickedDate ?? dueDayOf(data)} onChange={onPickDate} />
      )}
      <FilesAndLinksSection
        delivery={delivery}
        readOnly={!canWrite}
        removingIds={state.removingIds}
        onRemoveFile={state.removeFile}
        onRemoveLink={state.removeLink}
      />
    </Stack>
  )
}
